'use client';

import { forwardRef, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';

import { debounce } from '@/utils/common';
import { cn } from '@/utils/styles';

type IBorderTextFieldType = 'INPUT' | 'TEXTAREA_WITH_BOTTOM_LINE' | 'TEXTAREA' | 'NUMBER_INPUT';

interface IBorderTextFieldProps extends React.HTMLAttributes<
	HTMLInputElement | HTMLTextAreaElement
> {
	value?: string;
	placeholder?: string;
	customInputClassName?: React.HTMLAttributes<HTMLInputElement>['className'];
	customParentClassName?: React.HTMLAttributes<HTMLDivElement>['className'];
	onChangeValue?: (value: string) => void;
	editMode?: boolean;
	fillMode?: boolean;
	type: IBorderTextFieldType;
	disabled?: boolean;
	nonEditable?: boolean;
	maxLength?: number;
	debounceDuration?: number;
	// If true, the value will be updated automatically when initial value is changed
	autoUpdateValueOnChangeInitialValue?: boolean;
}

// Pasted U+00A0 never line-wraps, so long titles overflow. Rewritten on the element rather than
// in state alone, because React skips the commit when the cleaned string equals current state.
function normalizeNonBreakingSpaces(el: HTMLInputElement | HTMLTextAreaElement) {
	const value = el.value.replace(/\u00A0/g, ' ');
	return value;
}

// Auto-grow a textarea to fit its content without causing scroll jumps.
// Setting height to 'auto' momentarily collapses the element, forcing every
// scrollable ancestor (and the window) to reflow and re-clamp its scroll
// position. On mobile with the keyboard open and long text, this makes the
// screen jump on each keystroke. We snapshot the scroll offsets before the
// height recomputation and restore them immediately after so the caret stays put.
function autoGrowPreservingScroll(el: HTMLTextAreaElement) {
	const scrollPositions: Array<[HTMLElement, number]> = [];
	let ancestor = el.parentElement;
	while (ancestor) {
		if (ancestor.scrollHeight > ancestor.clientHeight) {
			scrollPositions.push([ancestor, ancestor.scrollTop]);
		}
		ancestor = ancestor.parentElement;
	}
	const windowScrollY = window.scrollY;

	el.style.height = 'auto';
	el.style.height = `${el.scrollHeight}px`;

	scrollPositions.forEach(([node, top]) => {
		node.scrollTop = top;
	});
	window.scrollTo({ top: windowScrollY });
}

function BorderTextField(
	{
		value,
		placeholder,
		customInputClassName,
		onChangeValue,
		editMode = false,
		customParentClassName,
		type,
		disabled = false,
		nonEditable = false,
		fillMode = false,
		maxLength,
		debounceDuration,
		autoUpdateValueOnChangeInitialValue = false,
		...props
	}: IBorderTextFieldProps,
	ref: React.ForwardedRef<HTMLInputElement | HTMLTextAreaElement | null>
) {
	const { onFocus: onFocusProp, onBlur: onBlurProp, ...restProps } = props;

	const [text, setText] = useState(value ?? '');
	const textAreaWithBottomLineRef = useRef<HTMLTextAreaElement | null>(null);
	// Tracks whether the field is currently focused. While the user is actively
	// typing we must NOT let an external `value` update overwrite local `text`,
	// otherwise a stale (debounced) value round-tripping back through the parent
	// clobbers the controlled value mid-keystroke and the caret jumps to the end.
	const isFocusedRef = useRef(false);

	const handleFocus = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		isFocusedRef.current = true;
		onFocusProp?.(e);
	};

	const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		isFocusedRef.current = false;
		onBlurProp?.(e);
	};

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const handleChangeValue = useCallback(
		debounce((value) => {
			onChangeValue?.(value as string);
		}, debounceDuration ?? 250),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[onChangeValue]
	);

	// Send a pending edit now rather than after the field is gone; a layout cleanup
	// runs before the form editor's passive cleanup drops its cache.
	useLayoutEffect(() => handleChangeValue.flush, [handleChangeValue]);

	const handleChangeTextWithBottomLine = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
		const value = normalizeNonBreakingSpaces(e.target);
		setText(value);
		handleChangeValue(value);
	};

	const handleChangeText = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
		const value = normalizeNonBreakingSpaces(e.target);
		setText(value);
		autoGrowPreservingScroll(e.target);
		handleChangeValue(value);
	};

	const handleChangeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = normalizeNonBreakingSpaces(e.target);
		setText(value);
		handleChangeValue(value);
	};

	const handleChangeNumberInput = (e: React.ChangeEvent<HTMLInputElement>) => {
		// First replace removes any non-digit and non-decimal characters
		// Second replace ensures only one decimal point
		// Third replace limits to 2 decimal places
		const value = e.target.value
			.replace(/[^\d.]/g, '')
			.replace(/\.(?=.*\.)/g, '')
			.replace(/(\.\d{2})\d+$/, '$1');
		setText(value);
		handleChangeValue(value);
	};

	useEffect(() => {
		const ele = textAreaWithBottomLineRef.current;

		if (!ele) return;

		setTimeout(() => autoGrowPreservingScroll(ele), 0);
	}, [value, text]);

	useEffect(() => {
		// Skip while focused so we never overwrite what the user is actively typing;
		// external/initial value changes (draft hydration, prefill) happen unfocused.
		if (autoUpdateValueOnChangeInitialValue && !isFocusedRef.current) {
			setText(value ?? '');
		}
	}, [value, autoUpdateValueOnChangeInitialValue]);

	return (
		<div className={cn('relative w-full', !!customParentClassName && customParentClassName)}>
			{type === 'TEXTAREA_WITH_BOTTOM_LINE' && (
				<>
					<Textarea
						value={text}
						disabled={(!editMode && !fillMode) || disabled || nonEditable}
						onChange={handleChangeTextWithBottomLine}
						rows={1}
						className={cn(
							`h-fit min-h-fit w-full resize-none overflow-hidden rounded-none border-none p-0 text-xl font-semibold text-primary outline-none placeholder:text-slate-400 focus-visible:ring-transparent focus-visible:ring-offset-0 disabled:cursor-default disabled:opacity-100`,
							!!customInputClassName && customInputClassName
						)}
						placeholder={placeholder}
						ref={(el) => {
							textAreaWithBottomLineRef.current = el;
							if (typeof ref === 'function') {
								ref(el); // Assign to forward ref if it's a function
							} else if (ref) {
								ref.current = el; // Assign to forward ref if it's an object
							}
						}}
						maxLength={maxLength}
						{...restProps}
						onFocus={handleFocus}
						onBlur={handleBlur}
					/>
				</>
			)}
			{type === 'TEXTAREA' && (
				<Textarea
					value={text}
					disabled={(!editMode && !fillMode) || disabled || nonEditable}
					onChange={handleChangeText}
					rows={1}
					className={cn(
						`transition-height h-fit min-h-20 w-full max-w-[540px] resize-none overflow-y-auto overflow-x-hidden border-input p-[10px] text-base leading-6 text-primary duration-200 disabled:opacity-100`,
						!!customInputClassName && customInputClassName
					)}
					placeholder={placeholder}
					maxLength={maxLength}
					ref={ref as React.RefObject<HTMLTextAreaElement>}
					{...restProps}
					onFocus={handleFocus}
					onBlur={handleBlur}
				/>
			)}
			{type === 'INPUT' && (
				<Input
					value={text}
					disabled={(!editMode && !fillMode) || disabled || nonEditable}
					onChange={handleChangeInput}
					placeholder={placeholder}
					className={cn(
						`w-full max-w-full border-input text-base leading-6 disabled:opacity-100`,
						!!customInputClassName && customInputClassName
					)}
					maxLength={maxLength}
					ref={ref as React.RefObject<HTMLInputElement>}
					{...restProps}
					onFocus={handleFocus}
					onBlur={handleBlur}
				/>
			)}
			{type === 'NUMBER_INPUT' && (
				<Input
					value={text}
					disabled={(!editMode && !fillMode) || disabled || nonEditable}
					onChange={handleChangeNumberInput}
					placeholder={placeholder}
					className={cn(
						`w-full max-w-[300px] border-input text-base leading-6 disabled:opacity-100`,
						!!customInputClassName && customInputClassName
					)}
					maxLength={maxLength}
					ref={ref as React.RefObject<HTMLInputElement>}
					{...restProps}
					onFocus={handleFocus}
					onBlur={handleBlur}
				/>
			)}
		</div>
	);
}

export default forwardRef(BorderTextField);
